import { useCallback, useState, useEffect, Fragment } from 'react';
import { object, string } from 'yup';
import { useFormik } from 'formik';
import { makeStyles } from '../../../themes';
import { FormModal } from './FormModal';
import { TextInput, CheckboxInput } from '../inputs';
import { useDispatch, addResource, fetchResourceById } from '../../../state';
import { AddCircleIcon, DeleteIcon, ErrorIcon } from '../../../assets';
import {
  makeuuid,
  packingMaterialTypes,
  updateNestedState,
} from '../../../lib';
import { CustomerPackingMaterialAPI, DiscountedRate } from '../../../types';
import { Button, Grid } from '@mui/material';

interface State {
  totalPrice: number | '';
  storageBillingRate: number | '';
  billableToCustomer: boolean;
  billingTieredRates: Record<string, DiscountedRate>;
}

const initialState: State = {
  totalPrice: '',
  storageBillingRate: '',
  billableToCustomer: true,
  billingTieredRates: {},
};

export function CustomerMaterialForm({
  open,
  handleClose,
  customerId,
  type,
  dimension,
  materialId,
  settingId,
  title,
  setViewVersion,
}) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState(initialState);

  const isPackageMaterial =
    type === packingMaterialTypes.PALLET || type === packingMaterialTypes.BOX;

  useEffect(() => {
    (async function () {
      if (settingId) {
        const { data } = await dispatch(
          fetchResourceById<CustomerPackingMaterialAPI>({
            baseUrl: '/customers',
            id: customerId,
            path: `material/${settingId}`,
          }),
        );
        if (data) {
          const {
            totalPrice,
            id,
            billableToCustomer,
            customerFeeOverride,
            storageBillingRate,
            billingTieredRates,
          } = data;
          setState((cur) => ({
            ...cur,
            totalPrice: customerFeeOverride !== null ? (totalPrice ?? '') : '',
            storageBillingRate: storageBillingRate ?? '',
            billableToCustomer,
            billingTieredRates:
              billingTieredRates?.reduce(
                (acc, cur) => {
                  acc[cur.id + ''] = cur;
                  return acc;
                },
                {} as Record<string, DiscountedRate>,
              ) ?? {},
            id,
          }));
        }
      }
    })();
  }, [settingId, customerId, dispatch]);

  const handleSubmit = useCallback(
    async (values: State) => {
      setSubmitting(true);
      const { error } = await dispatch(
        addResource({
          baseUrl: `/customers/${customerId}/material`,
          payload: {
            ...values,
            customerId,
            packingMaterialID: materialId,
            typeToUpdate: type,
            dimensionToUpdate: dimension,
            billingTieredRates: values.billingTieredRates
              ? Object.values(values.billingTieredRates)
              : [],
          },
          message: 'Changes saved',
        }),
      );
      setSubmitting(false);
      if (!error) {
        setViewVersion((cur) => ++cur);
        handleClose();
      }
    },
    [
      customerId,
      dimension,
      dispatch,
      handleClose,
      materialId,
      setViewVersion,
      type,
    ],
  );

  const formik = useFormik({
    initialValues: { ...initialState, ...state },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  const handleTieredChange = (discountKey: string) => (e) => {
    const { name, value } = e.target;

    const newState = updateNestedState({
      nestedKey: ['billingTieredRates', discountKey, name],
      value,
      state: formik.values,
    });
    formik.setValues({ ...formik.values, ...newState });
  };

  function removeTieredRate(tieredRateKey: string) {
    const allTieredRates = formik.values.billingTieredRates;
    if (!allTieredRates) return;
    const { [tieredRateKey]: idToRemove, ...rest } = allTieredRates;
    const newState = {
      ...formik.values,
      billingTieredRates: rest,
    };
    formik.setValues({ ...formik.values, ...newState });
  }

  function handleAddTieredRate() {
    const id = makeuuid(7);
    const newState = updateNestedState({
      nestedKey: ['billingTieredRates', id],
      value: { ...initialState.billingTieredRates[id] },
      state: formik.values,
    });
    formik.setValues({ ...formik.values, ...newState });
  }

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={
        <div className={classes.titleContainer}>
          <span className={classes.title}>Edit material</span>
          {!!title && <span className={classes.subTitle}>{title}</span>}
        </div>
      }
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      paperProps={{ style: { width: 468 } }}
      submitting={submitting}
    >
      <Fragment>
        {!!type && (
          <div className={classes.warningTextContainer}>
            <ErrorIcon className={classes.errorIcon} />
            <span className={classes.warningText}>
              This will override all items in this category
            </span>
          </div>
        )}
        <TextInput
          name='totalPrice'
          formikProps={formik}
          label='Customer price'
          className={classes.input}
          format='currency'
          required={
            formik.values.billableToCustomer &&
            !formik.values.storageBillingRate
          }
        />
        <CheckboxInput
          formikProps={formik}
          checkboxes={[
            {
              labelProps: {
                label: 'Billable to customer',
              },
              checkboxProps: {
                name: 'billableToCustomer',
              },
            },
          ]}
        />
        {isPackageMaterial && (
          <Fragment>
            <TextInput
              name='storageBillingRate'
              formikProps={formik}
              label='Storage billing rate'
              className={classes.input}
              format='currency'
            />
            {!!formik.values.storageBillingRate &&
              +formik.values.storageBillingRate > 0 && (
                <Fragment>
                  <div>
                    <span className={classes.discountText}>Tiered rates</span>
                    {Object.keys(formik.values.billingTieredRates).map((k) => {
                      const { oversizedDimension, rate } =
                        formik.values.billingTieredRates[k];
                      return (
                        <div
                          key={k}
                          className={classes.discountedRatesContainer}
                        >
                          <Grid
                            container
                            spacing={2}
                            rowSpacing={2}
                            className={classes.feeGridContainer}
                          >
                            <Grid
                              size={{
                                xs: 12,
                                sm: 4,
                              }}
                            >
                              <TextInput
                                name='oversizedDimension'
                                value={oversizedDimension}
                                label='Height(inches)'
                                handleChange={handleTieredChange(k)}
                                type='number'
                                required
                              />
                            </Grid>
                            <Grid
                              size={{
                                xs: 12,
                                sm: 4,
                              }}
                            >
                              <TextInput
                                name='rate'
                                value={rate}
                                label='Rate'
                                handleChange={handleTieredChange(k)}
                                format='currency'
                                required
                              />
                            </Grid>
                            <Grid
                              className={classes.deleteGridItem}
                              size={{
                                xs: 12,
                                sm: 4,
                              }}
                            >
                              <DeleteIcon
                                onClick={() => removeTieredRate(k)}
                                style={{ marginTop: 16 }}
                              />
                            </Grid>
                          </Grid>
                        </div>
                      );
                    })}
                  </div>
                  <Button
                    startIcon={<AddCircleIcon />}
                    color='primary'
                    size='small'
                    onClick={() => handleAddTieredRate()}
                    className={classes.addBtn}
                  >
                    tiered rate
                  </Button>
                </Fragment>
              )}
          </Fragment>
        )}
      </Fragment>
    </FormModal>
  );
}

const schema = object().shape({
  totalPrice: string()
    .nullable()
    .when(['billableToCustomer', 'storageBillingRate'], {
      is: (billableToCustomer, storageBillingRate) =>
        billableToCustomer && !storageBillingRate,
      then: (schema) => schema.required('Required'),
    }),
});

const useStyles = makeStyles()((theme) => ({
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  subTitle: {
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 500,
  },
  input: {
    marginBottom: 16,
    width: 300,
  },
  warningTextContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  errorIcon: {
    fontSize: 18,
    color: '#FFBD00',
    background: '#FFFFFF',
    marginRight: 6,
  },
  warningText: {
    color: '#A5AABD',
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 500,
  },
  feeGridContainer: {
    maxWidth: 788,
  },
  discountedRatesContainer: {
    backgroundColor: '#F9FAFC',
  },
  discountText: {
    fontFamily: 'Montserrat',
    fontSize: 12,
    fontStyle: 'italic',
    fontWeight: 'bold',
  },
  deleteGridItem: {
    textAlign: 'right',
    color: '#D5D8EC',
    '& .MuiSvgIcon-root': {
      cursor: 'pointer',
    },
  },
  addBtn: {
    textTransform: 'none',
    marginTop: 10,
    marginBottom: 10,
  },
}));
